import moment from 'moment-timezone';
import { BookingServiceClient, InvitationsClient, UserServiceClient } from 'fitbeat.api-clients';
import { ProgramType } from 'fitbeat.models';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { getApiConfig } from '../config';
import { GoogleAnalytics } from './google-analytics';
import { getTimezoneFromGymName } from '../utils/gyms';

export class WorkoutSessionService {
  googleAnalytics = new GoogleAnalytics();

  bookingServiceClient = new BookingServiceClient(getApiConfig(), fetch, new AuthTokenBuilder(), this.googleAnalytics);

  userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    console.log,
    new AuthTokenBuilder(),
    this.googleAnalytics,
  );

  invitationClient = new InvitationsClient(
    getApiConfig(),
    fetch,
    console.log,
    new AuthTokenBuilder(),
    this.googleAnalytics,
  );

  constructor(authProvider, setIsReady) {
    this.firebaseUserId = authProvider.getFirebaseUserId();
    this.authProvider = authProvider;
    this.setIsReady = setIsReady;

    this.currentBookings = [];
    this.isUpdating = false;
  }

  async init(userProfile) {
    console.log('user_id', this.firebaseUserId);
    this.userLongTermGoal = userProfile.longTermGoal;
    await this.getMyCurrentBookings();

    this.setIsReady(true);
    this.setIsUpdating(false);
  }

  syncData = async () => {
    if (this.isUpdating) {
      return;
    }
    await this.getMyCurrentBookings();
  };

  updateUserLongTermGoal = (longTermGoal = ProgramType.NotSet) => {
    this.userLongTermGoal = longTermGoal;
  };

  getMyCurrentBookings = async () => {
    try {
      this.setIsUpdating(true);
      const bookings = await this.bookingServiceClient.getMyBookings(this.userLongTermGoal);

      if (JSON.stringify(bookings) !== JSON.stringify(this.currentBookings)) {
        this.currentBookings = bookings;
      }

      return bookings;
    } catch (error) {
      // fail silently
    } finally {
      this.setIsUpdating(false);
    }
  };

  getMyWorkoutHistory = async () => {
    try {
      this.setIsUpdating(true);
      const history = await this.bookingServiceClient.getMyWorkoutHistory();
      return history;
    } finally {
      this.setIsUpdating(false);
    }
  };

  getHomeAvailableBookings = async (from, gymName) => {
    const timezone = moment.tz.guess();
    const data = await this.bookingServiceClient.getHomeAvailableBookings(from, gymName, timezone);
    return data;
  };

  makeBooking = async (
    withPartner = false,
    slot,
    gymName,
    workoutSettings,
    isStudioDemoBooking = false,
    playbackSpeed,
    livestreamOptions,
    isStreamingWorkout,
    areaCategory,
  ) => {
    try {
      const timezone = getTimezoneFromGymName(gymName);
      const isDemo = slot.isDemo ? slot.isDemo : false;
      this.setIsUpdating(true);
      const options = {
        withPartner,
        startDateUTC: slot.startTime,
        workoutType: slot.workoutType,
        gymName,
        isInstant: slot.isInstant,
        isDemo,
        timezone,
        workoutSettings,
        isStudioDemo: isStudioDemoBooking,
        playbackSpeed,
        livestreamOptions,
        isStreamingWorkout,
        areaCategory,
      };
      const responseJson = await this.bookingServiceClient.makeBooking(options);
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  bookAndInvite = async (partnerId, slot, gymName, workoutSettings, areaCategory) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.bookingServiceClient.makeBookingWithPartnerAndInvite(
        partnerId,
        slot.startTime,
        slot.workoutType,
        gymName,
        workoutSettings,
        areaCategory,
      );
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  cancelBooking = async (workoutSessionId) => {
    try {
      this.setIsUpdating(true);
      await this.bookingServiceClient.cancelBookingAndReturnBookingList(workoutSessionId);
      const bookings = await this.getMyCurrentBookings();
      return bookings;
    } finally {
      this.setIsUpdating(false);
    }
  };

  changeBooking = async (workoutSessionId, changeToDateUTC, workoutType, gymName, workoutSettings, isStudioDemo) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.bookingServiceClient.changeBooking(
        this.firebaseUserId,
        workoutSessionId,
        changeToDateUTC,
        workoutType,
        gymName,
        workoutSettings,
        isStudioDemo,
      );
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  sendInvite = async (partnerId, bookingId) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.invitationClient.changeInvite(bookingId, partnerId);
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  acceptInvite = async (provisionalId, acceptInviterSettings) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.invitationClient.acceptInvite(provisionalId, acceptInviterSettings);
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  cancelInvite = async (provisionalId) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.invitationClient.cancelInvite(provisionalId);
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  rejectInvite = async (provisionalId) => {
    try {
      this.setIsUpdating(true);
      const responseJson = await this.invitationClient.rejectInvite(provisionalId);
      return responseJson;
    } finally {
      this.setIsUpdating(false);
    }
  };

  blockPartnerAndRejectInvite = async (invite, partnerId) => {
    try {
      this.setIsUpdating(true);

      await this.rejectInvite(invite.uniqueReference);
      await this.userServiceClient.addPartnerToBlacklist(partnerId);

      const bookings = await this.getMyCurrentBookings();
      return bookings;
    } finally {
      this.setIsUpdating(false);
    }
  };

  resetWorkoutExercises = async (
    workoutSessionId,
    workoutSettings,
    shouldExcludeFromProgram,
    shouldSaveDefaultEquipment,
  ) => {
    try {
      this.setIsUpdating(true);
      await this.bookingServiceClient.resetWorkoutSettings(
        workoutSessionId,
        workoutSettings,
        shouldExcludeFromProgram,
        shouldSaveDefaultEquipment,
      );
      const bookings = await this.getMyCurrentBookings();
      return bookings;
    } finally {
      this.setIsUpdating(false);
    }
  };

  getRecommendedSettingsForDate = async (workoutDate, userProgram, gymName) => {
    try {
      this.setIsUpdating(true);
      return this.bookingServiceClient.getRecommendedSettingsForDate(workoutDate, userProgram, gymName);
    } finally {
      this.setIsUpdating(false);
    }
  };

  setIsUpdating = (isUpdating) => {
    this.isUpdating = isUpdating;
  };

  getLastWeekWorkoutHistory = async (from) => {
    try {
      this.setIsUpdating(true);
      const history = await this.bookingServiceClient.getLastWeekWorkoutHistory(from);
      return history;
    } finally {
      this.setIsUpdating(false);
    }
  };
}
